"use client";

import React, { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import HowItWorks from "../HomePage/HowItWorks";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

// ─── Grid styles ──────────────────────────────────────────────────────────────
const lightGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const darkGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const greenOrangeGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.10) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const ourWorkSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Our Work – Fiieve Portfolio",
  "description": "Browse Fiieve's portfolio of 36+ successful web development, mobile app, IoT, SaaS, AI, and engineering projects that have helped businesses grow and scale.",
  "url": "https://fiieve.com/portfolio",
  "mainEntity": {
    "@type": "ItemList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "item": { "@type": "CreativeWork", "name": "Cleaning Service Website",      "url": "https://cleaning-website-khaki.vercel.app/" } },
      { "@type": "ListItem", "position": 2, "item": { "@type": "CreativeWork", "name": "Security Service Website",      "url": "https://security-website-hazel.vercel.app/" } },
      { "@type": "ListItem", "position": 3, "item": { "@type": "CreativeWork", "name": "Ejazify – Tutoring Platform",   "url": "https://ejazify.org/" } },
      { "@type": "ListItem", "position": 4, "item": { "@type": "CreativeWork", "name": "Wedding Planning Website",      "url": "https://wedding-planning-website.vercel.app/" } },
      { "@type": "ListItem", "position": 5, "item": { "@type": "CreativeWork", "name": "E-Commerce Shoe Store",         "url": "https://e-commerce-shoes-website-tau.vercel.app/" } },
      { "@type": "ListItem", "position": 6, "item": { "@type": "CreativeWork", "name": "Car Rental Platform",           "url": "https://car-rental-website-home-page.vercel.app/" } },
    ],
  },
  "provider": { "@type": "Organization", "name": "Fiieve", "url": "https://fiieve.com" },
});

// ─── Filter categories ────────────────────────────────────────────────────────
const FILTER_CATEGORIES = [
  "All", "Web", "Mobile", "Figma Designs", "IoT", "SaaS", "Embedded", "AI", "Robotics",
];

// ─── Portfolio items ──────────────────────────────────────────────────────────
const ALL_PORTFOLIO_ITEMS = [
  // ── Web ──
  {
    id: 1,
    title: "Cleaning Service Website",
    category: "Web",
    description: "Professional cleaning service website with service listings, booking, and trust-building testimonials.",
    image: "/Images/cleaning.png",
    link: "https://cleaning-website-khaki.vercel.app/",
    caseStudySlug: "cleaning-service-website",
    techStack: "React.js, Tailwind CSS",
  },
  {
    id: 2,
    title: "Security Service Website",
    category: "Web",
    description: "A robust security company website featuring service breakdowns, emergency contact integration, and client trust signals.",
    image: "/Images/securitywebsite.png",
    link: "https://security-website-hazel.vercel.app/",
    caseStudySlug: "security-service-website",
    techStack: "React.js, Tailwind CSS, Node.js",
  },
  {
    id: 3,
    title: "Ejazify – Tutoring Platform",
    category: "Web",
    description: "An online knowledge-sharing platform connecting tutors with students through interactive courses.",
    image: "/Images/shareyourknowledge.png",
    link: "https://ejazify.org/",
    caseStudySlug: "ejazify-tutoring-platform",
    techStack: "React.js, Tailwind CSS, Node.js",
  },
  {
    id: 4,
    title: "Wedding Planning Website",
    category: "Web",
    description: "Full-featured wedding planning platform with vendor management, RSVP tracking, and event timelines.",
    image: "/Images/weddingplanningwebsite.png",
    link: "https://wedding-planning-website.vercel.app/",
    caseStudySlug: "wedding-planning-website",
    techStack: "React.js, Tailwind CSS, Firebase",
  },
  {
    id: 5,
    title: "E-Commerce Shoe Store",
    category: "Web",
    description: "Modern e-commerce storefront with product catalogue, cart, checkout, and payment integration.",
    image: "/Images/shoewebsite.png",
    link: "https://e-commerce-shoes-website-tau.vercel.app/",
    caseStudySlug: "ecommerce-shoe-store",
    techStack: "Next.js, Tailwind CSS, MongoDB",
  },
  {
    id: 6,
    title: "Car Rental Platform",
    category: "Web",
    description: "A sleek car rental landing page with search, booking flow, and responsive design built for conversion.",
    image: "/Images/carrentalpage.png",
    link: "https://car-rental-website-home-page.vercel.app/",
    caseStudySlug: "car-rental-platform",
    techStack: "React.js, Tailwind CSS, Node.js",
  },

  // ── Figma Designs ──
  {
    id: 7,
    title: "Cleaning Website — Design 1",
    category: "Figma Designs",
    description: "A modern, eco-friendly cleaning service website design featuring service cards, testimonials, and a strong call-to-action layout.",
    image: "/Images/figma-cleaning-1.png",
     link: "https://www.figma.com/design/e9Sy7ZHz6mrp2FHU4vDy35/Cleaning-Website--Community-?node-id=0-1&t=YUMhagXXILCRtQ0D-1",
    caseStudySlug: "figma-cleaning-website-1",
    techStack: "Figma, Auto Layout, Components",
  },
  {
    id: 8,
    title: "Cleaning Website — Design 2",
    category: "Figma Designs",
    description: "A professional cleaning brand design with a trust-focused layout, pricing section, and multi-service showcase.",
    image: "/Images/figma-cleaning-2.png",
   link: "https://www.figma.com/design/MadBpMfPANb9barI2XlByI/Cleaning-Website--Home--About-Us--Contact-Us--Services---Service-Detail---Community-?node-id=0-1&t=o26DyoHba2JCq0Kv-1",
    caseStudySlug: "figma-cleaning-website-2",
    techStack: "Figma, Auto Layout, Components",
  },
  {
    id: 9,
    title: "Cleaning Website — Design 3",
    category: "Figma Designs",
    description: "A bold, vibrant cleaning service website concept with an animated section layout and strong visual hierarchy.",
    image: "/Images/figma-cleaning-3.png",
      link: "https://www.figma.com/design/i9yCtFCPG3OjUI1zzSfYii/cleaning-website--Community-?node-id=0-1&t=HPDoy2MDRT8SA6Mb-1",
    caseStudySlug: "figma-cleaning-website-3",
    techStack: "Figma, Auto Layout, Components",
  },
  {
    id: 10,
    title: "Corporate Technology Website",
    category: "Figma Designs",
    description: "A sleek corporate technology company website design with dark aesthetics, service sections, and enterprise-focused UI patterns.",
    image: "/Images/figma-corporate-tech.png",
   link: "https://www.figma.com/design/71aaujIeTdZBMQoWPdGldr/Corporate-Technology-Services-Website--Community-?node-id=0-1&t=15f50hBG0kzoztiH-1",
    caseStudySlug: "figma-corporate-technology-website",
    techStack: "Figma, Auto Layout, Design System",
  },
  {
    id: 11,
    title: "Security Service Website",
    category: "Figma Designs",
    description: "A high-trust security company design featuring emergency contact, service breakdowns, and authority-building visual elements.",
    image: "/Images/figma-security.png",
    link: "https://www.figma.com/design/IJCuUpivNqxW6zG0XJr4Mx/Security-Website--Community-?node-id=0-1&t=2taQdreJyXyZssZ8-1",
    caseStudySlug: "figma-security-website",
    techStack: "Figma, Auto Layout, Prototyping",
  },
  {
    id: 12,
    title: "Industrial Website",
    category: "Figma Designs",
    description: "A robust industrial company website design with a strong grid layout, capability showcases, and B2B-focused section structure.",
    image: "/Images/figma-industrial.png",
    link: "https://www.figma.com/design/nHe5HXfyJUlgegBqPqkMhB/Industrial-Website--Community-?node-id=0-1&t=hp4pOhx2FbLJHlfn-1",
    caseStudySlug: "figma-industrial-website",
    techStack: "Figma, Auto Layout, Design System",
  },
  {
    id: 13,
    title: "E-Learning Website",
    category: "Figma Designs",
    description: "A clean, modern e-learning platform design featuring course listings, instructor profiles, student testimonials, and an intuitive learning dashboard.",
    image: "/Images/figma-elearning.png",
     link: "https://www.figma.com/design/CDbsRcUcBEIANFzW6Elfos/E-Learning-Website--Community-?node-id=0-1&t=TIcumg952eOhwJX7-1",
    caseStudySlug: "figma-elearning-website",
    techStack: "Figma, Auto Layout, Prototyping",
  },
];

// ─── CountUp ──────────────────────────────────────────────────────────────────
function CountUp({ target, duration = 1200 }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!inView) return;
    if (target.includes("–") || target.includes("/")) {
      const timeout = setTimeout(() => setDisplay(target), 400);
      return () => clearTimeout(timeout);
    }
    const numeric = parseFloat(target.replace(/[^\d.]/g, ""));
    const suffix  = target.replace(/[\d.]/g, "");
    const isDecimal = target.includes(".");
    let start = 0;
    const inc = numeric / (duration / 16);
    const timer = setInterval(() => {
      start += inc;
      if (start >= numeric) {
        setDisplay(`${isDecimal ? numeric.toFixed(1) : numeric}${suffix}`);
        clearInterval(timer);
      } else {
        setDisplay(`${isDecimal ? start.toFixed(1) : Math.floor(start)}${suffix}`);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{display}</span>;
}

// ─── FlipCard ─────────────────────────────────────────────────────────────────
const FlipCard = ({ item, cardBackBg }) => (
  <div
    className="group"
    style={{ perspective: "1000px", height: "320px" }}
    itemScope
    itemType="https://schema.org/CreativeWork"
  >
    <div
      style={{
        position: "relative", width: "100%", height: "100%",
        transformStyle: "preserve-3d",
        transition: "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
      className="group-hover:[transform:rotateY(180deg)]"
    >
      {/* FRONT */}
      <div
        style={{ position: "absolute", inset: 0, backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}
        className="rounded-xl overflow-hidden border border-green-400/30"
      >
        <img
          src={item.image}
          alt={`${item.title} – ${item.category} project by Fiieve`}
          loading="lazy"
          width="400"
          height="320"
          className="w-full h-full object-cover"
          itemProp="image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" aria-hidden="true" />
      </div>

      {/* BACK */}
      <div
        style={{
          position: "absolute", inset: 0,
          backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden",
          transform: "rotateY(180deg)",
        }}
        className={`rounded-xl border border-green-400/40 ${cardBackBg} flex flex-col items-center justify-center gap-4 px-5 text-center`}
      >
        <h3 className="text-lg md:text-xl font-semibold text-green-300" itemProp="name">
          {item.title}
        </h3>
        <p className="text-gray-400 text-sm leading-relaxed line-clamp-2" itemProp="description">
          {item.description}
        </p>
        <div className="w-full text-center">
          <p className="text-gray-300 text-xs font-semibold uppercase tracking-widest mb-2">Tech Stack</p>
          <ul className="flex flex-wrap items-center justify-center gap-2">
            {item.techStack.split(",").map((tech) => (
              <li key={tech.trim()} className="flex items-center gap-1.5 text-gray-200 text-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" aria-hidden="true" />
                {tech.trim()}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-3 mt-1">
          {item.link !== "#" ? (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View live ${item.title} project`}
              className="inline-flex items-center gap-1.5 bg-green-400 text-black font-semibold text-sm px-5 py-2 rounded-full hover:bg-green-300 transition"
              itemProp="url"
            >
              Live Demo <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          ) : (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`View ${item.title} Figma design`}
              className="inline-flex items-center gap-1.5 bg-green-400 text-black font-semibold text-sm px-5 py-2 rounded-full hover:bg-green-300 transition"
              itemProp="url"
            >
              View Design <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            </a>
          )}
          <a
            href={`/portfolio/${item.caseStudySlug}`}
            aria-label={`Read ${item.title} case study`}
            className="inline-flex items-center gap-1.5 border border-green-400/60 text-green-400 font-semibold text-sm px-5 py-2 rounded-full hover:bg-green-400/10 transition"
          >
            Case Study <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
        </div>
      </div>
    </div>
  </div>
);

// ─── Coming Soon ──────────────────────────────────────────────────────────────
const ComingSoonState = ({ category }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.4 }}
    className="col-span-full flex flex-col items-center justify-center py-20 text-center"
  >
    <div className="w-20 h-20 mb-6 rounded-full bg-green-400/10 border border-green-400/30 flex items-center justify-center text-3xl">
      🚀
    </div>
    <h3 className="text-xl md:text-2xl font-bold text-green-400 mb-2">
      {category} Projects Coming Soon
    </h3>
    <p className="text-gray-400 max-w-md text-sm md:text-base mb-6">
      We're currently working on exciting {category.toLowerCase()} projects.
      Check back soon or get in touch to discuss your {category.toLowerCase()} project.
    </p>
    <a
      href="/contact-us"
      className="inline-flex items-center gap-2 bg-green-400 text-black font-semibold text-sm px-6 py-2.5 rounded-full hover:bg-green-300 transition"
    >
      Discuss a {category} Project
      <ChevronRight className="w-4 h-4" aria-hidden="true" />
    </a>
  </motion.div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const OurWorkSection = () => {
  const navigate = useNavigate();
  const { isDark, theme } = useTheme();
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const isGreenOrange = theme === "greenOrange";
  const isLight       = !isDark && !isGreenOrange;

  const sectionBg   = isDark ? "bg-black text-white" : isGreenOrange ? "bg-[#0d1a0e] text-white" : "bg-[#f0f4f0] text-gray-900";
  const sectionGrid = isLight ? lightGridStyle : isGreenOrange ? greenOrangeGridStyle : darkGridStyle;
  const headingText = isDark || isGreenOrange ? "text-white"    : "text-gray-900";
  const subText     = isDark || isGreenOrange ? "text-gray-400" : "text-gray-500";
  const accentText  = isGreenOrange ? "text-green-400" : isDark ? "text-green-500" : "text-green-700";
  const cardBackBg  = isDark ? "bg-[#0a1a0a]" : isGreenOrange ? "bg-[#0a1a0a]" : "bg-[#1a2e1a]";
  const statCardBg  = isDark ? "bg-gray-900/80" : "bg-[#1a2e1a]";
  const statGlow    = "0 0 30px rgba(34,197,94,0.6)";

  // filter vars removed — using named sections now

  const filterBarBg        = "bg-[#0f1f0f]/90 border border-green-400/20";
  const filterItemBase     = "px-4 md:px-5 py-2.5 text-sm font-medium transition-all duration-300 cursor-pointer select-none";
  const filterItemActive   = "text-green-400";
  const filterItemInactive = "text-gray-300 hover:text-green-400";

  // Items for "All" view — grouped by category
  const webItems    = ALL_PORTFOLIO_ITEMS.filter(i => i.category === "Web");
  const figmaItems  = ALL_PORTFOLIO_ITEMS.filter(i => i.category === "Figma Designs");
  const otherItems  = ALL_PORTFOLIO_ITEMS.filter(i => i.category !== "Web" && i.category !== "Figma Designs");

  // For individual category filters (not All)
  const filteredItems = activeFilter === "All"
    ? []
    : ALL_PORTFOLIO_ITEMS.filter(i => i.category === activeFilter);

  const stats = [
    { number: "98%", label: "Client Satisfaction",    icon: "/Images/satisfaction.png" },
    { number: "40%", label: "Average ROI Increase",   icon: "/Images/roi.png" },
    { number: "36+", label: "Successful Projects",    icon: "/Images/projects.png" },
    { number: "50+", label: "Award-winning Projects", icon: "/Images/award.png" },
  ];



  return (
    <>
      <SEOHead
        title="Our Work – Web Design & Development Portfolio | Fiieve"
        description="Explore Fiieve's portfolio of 36+ successful digital projects including web design, e-commerce, mobile apps, IoT, SaaS, and AI automation. See real results from our UK-based software engineering team."
        canonical="https://fiieve.com/portfolio"
        ogImage="https://fiieve.com/Images/og-our-work.jpg"
        schema={ourWorkSchema}
      />

      <main
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${sectionBg}`}
        style={sectionGrid}
      >
        {isLight && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }} aria-hidden="true">
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.11) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.09) 0%, transparent 70%)", filter:"blur(45px)" }} />
          </div>
        )}
        {isGreenOrange && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 0 }} aria-hidden="true">
            <div style={{ position:"absolute", top:"5%", left:"-8%", width:"45vw", height:"45vw", background:"radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter:"blur(45px)" }} />
            <div style={{ position:"absolute", top:"5%", right:"-8%", width:"40vw", height:"40vw", background:"radial-gradient(circle, rgba(251,146,60,0.14) 0%, transparent 70%)", filter:"blur(45px)" }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ── HERO ── */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">Our Portfolio</h1>
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white" role="list">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Our Portfolio</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ── STATS ── */}
          <section aria-labelledby="stats-heading" className="px-4 md:px-8 lg:px-16 py-12 md:py-14">
            <motion.div initial={{ opacity: 0, y: 80 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: "easeOut" }} viewport={{ once: true, amount: 0.3 }}>
              <div className="max-w-6xl mx-auto">
                <h2 id="stats-heading" className={`text-3xl md:text-4xl font-bold text-center mb-4 ${headingText}`}>
                  <span className={accentText}>Transforming Business</span> with
                  <br />results-driven solutions
                </h2>
                <p className={`text-center mb-10 max-w-2xl mx-auto ${subText}`}>
                  We've helped 80+ businesses increase revenue, reduce costs, and improve efficiency through our proven digital solutions.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 40 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.2, ease: "easeOut" }}
                      viewport={{ once: true, amount: 0.4 }}
                      whileHover={{ scale: 1.08, boxShadow: statGlow }}
                      className={`border border-green-500/50 hover:border-green-500 rounded-lg p-5 md:p-6 text-center transition-colors duration-300 ${statCardBg} flex flex-col items-center w-[90%] mx-auto`}
                    >
                      <img src={stat.icon} alt={stat.label} className="w-16 h-16 md:w-20 md:h-20 mb-4 object-contain" loading="lazy" width="80" height="80" />
                      <div className="text-3xl md:text-4xl font-bold text-green-500 mb-2">
                        <CountUp target={stat.number} />
                      </div>
                      <p className="text-sm md:text-base font-semibold text-gray-300">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </section>

          {/* ── PORTFOLIO ── */}
          <section aria-labelledby="portfolio-heading" className="px-4 md:px-8 lg:px-16 py-8 md:py-10 -mt-8">
            <div className="max-w-6xl mx-auto">

              {/* Section intro */}
              <motion.div initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 id="portfolio-heading" className={`text-3xl md:text-4xl font-bold mb-3 ${headingText}`}>
                  Our Work <span className={accentText}>Speaks for Itself</span>
                </h2>
                <p className={`max-w-2xl mx-auto ${subText}`}>
                  Explore our portfolio of web applications, mobile apps, Figma designs, IoT systems, and engineering solutions.
                </p>
              </motion.div>

              {/* Filter Bar */}
              <motion.nav
                aria-label="Project category filter"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className={`flex flex-wrap items-center justify-center rounded-full ${filterBarBg} backdrop-blur-sm w-fit mx-auto mb-12 px-2 py-1`}
              >
                {FILTER_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveFilter(cat)}
                    aria-pressed={activeFilter === cat}
                    className={`${filterItemBase} ${activeFilter === cat ? filterItemActive : filterItemInactive}`}
                  >
                    {cat}
                  </button>
                ))}
              </motion.nav>

              {/* ── ALL view: grouped sub-sections ── */}
              {activeFilter === "All" && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key="all"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Web Development */}
                    {webItems.length > 0 && (
                      <div className="mb-14">
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex-1 h-px bg-green-400/20" />
                          <h3 className={`text-xl md:text-2xl font-bold ${headingText} whitespace-nowrap`}>🌐 Web Development</h3>
                          <div className="flex-1 h-px bg-green-400/20" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {webItems.map((item, index) => (
                            <motion.div key={item.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}>
                              <FlipCard item={item} cardBackBg={cardBackBg} />
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Figma Designs */}
                    {figmaItems.length > 0 && (
                      <div className="mb-14">
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex-1 h-px bg-green-400/20" />
                          <h3 className={`text-xl md:text-2xl font-bold ${headingText} whitespace-nowrap`}>🎨 Figma Designs</h3>
                          <div className="flex-1 h-px bg-green-400/20" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {figmaItems.map((item, index) => (
                            <motion.div key={item.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}>
                              <FlipCard item={item} cardBackBg={cardBackBg} />
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Other categories (Mobile, IoT etc if any) */}
                    {otherItems.length > 0 && (
                      <div>
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex-1 h-px bg-green-400/20" />
                          <h3 className={`text-xl md:text-2xl font-bold ${headingText} whitespace-nowrap`}>📱 More Projects</h3>
                          <div className="flex-1 h-px bg-green-400/20" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {otherItems.map((item, index) => (
                            <motion.div key={item.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}>
                              <FlipCard item={item} cardBackBg={cardBackBg} />
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              )}

              {/* ── Individual category filter view ── */}
              {activeFilter !== "All" && (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeFilter}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    {/* Sub-section heading */}
                    <div className="flex items-center gap-4 mb-8">
                      <div className="flex-1 h-px bg-green-400/20" />
                      <h3 className={`text-xl md:text-2xl font-bold ${headingText} whitespace-nowrap`}>
                        {activeFilter === "Web" ? "🌐" : activeFilter === "Figma Designs" ? "🎨" : activeFilter === "Mobile" ? "📱" : activeFilter === "IoT" ? "🔌" : activeFilter === "AI" ? "🤖" : "💡"} {activeFilter}
                      </h3>
                      <div className="flex-1 h-px bg-green-400/20" />
                    </div>

                    {filteredItems.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredItems.map((item, index) => (
                          <motion.div key={item.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.12 }} viewport={{ once: true }}>
                            <FlipCard item={item} cardBackBg={cardBackBg} />
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <ComingSoonState category={activeFilter} />
                    )}
                  </motion.div>
                </AnimatePresence>
              )}

            </div>
          </section>

          {/* ── CTA ── */}
          <section aria-label="Call to action" className="px-4 md:px-8 lg:px-16 py-4 md:py-6">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} viewport={{ once: true }}>
              <div className={`max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 rounded-2xl px-6 md:px-10 py-5 md:py-6 border border-green-400/30 ${isDark ? "bg-gray-900/60" : isGreenOrange ? "bg-[#0a1a0a]/70" : "bg-[#1a2e1a]"} backdrop-blur-sm`}>
                <div className="text-center md:text-left">
                  <h2 className="text-lg md:text-xl font-bold text-white">
                    Have a Similar Project?{" "}
                    <span className="text-green-400">Let's Talk.</span>
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">
                    Web apps, mobile solutions, IoT, or AI — we'd love to hear your idea.
                  </p>
                </div>
                <button
                  onClick={() => navigate("/contact-us")}
                  className="flex-shrink-0 inline-flex items-center gap-2 bg-green-400 hover:bg-green-300 text-black font-bold text-sm px-6 py-2.5 rounded-full transition-all duration-300 hover:scale-105"
                  aria-label="Discuss your project with Fiieve"
                >
                  Discuss Your Project
                  <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </motion.div>
          </section>

          {/* ── HOW IT WORKS ── */}
          <motion.div initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} viewport={{ once: true }}>
            <HowItWorks />
          </motion.div>

        </div>
      </main>
    </>
  );
};

export default OurWorkSection;